import React, { useState, useEffect, useRef } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { 
  requestDevnetSol,
  isEligibleForAirdrop,
  getTimeUntilNextAirdrop,
  formatTimeRemaining,
  recordFailedAttempt
} from '../utils/faucet';

// Icon component for the faucet button has been replaced with an image

export const FaucetButton: React.FC = () => {
  const { connection } = useConnection();
  const { publicKey } = useWallet();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isFaucetAvailable, setIsFaucetAvailable] = useState(true);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [isFaucetAnimated, setIsFaucetAnimated] = useState(false);

  // Reference to keep track of the timer interval
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(0);

  // Start or update the countdown timer
  const startOrUpdateTimer = (remaining: number) => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    // Only start timer if there's time remaining
    if (remaining <= 0) {
      setIsTimerActive(false);
      setTimeRemaining(0);
      setIsFaucetAvailable(true);
      return;
    }
    
    // Set initial state
    setTimeRemaining(remaining);
    setIsTimerActive(true);
    setIsFaucetAvailable(false);
    
    // Start the timer interval
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prevTime => {
        // Calculate new time considering real elapsed time
        const now = Date.now();
        const elapsed = now - lastCheckRef.current;
        lastCheckRef.current = now;
        
        // Reduce by actual elapsed time, minimum 1 second
        const reduction = Math.max(elapsed, 1000);
        const newTime = prevTime - reduction;
        
        if (newTime <= 0) {
          clearInterval(timerIntervalRef.current!);
          setIsTimerActive(false);
          setIsFaucetAvailable(true);
          return 0;
        }
        
        return newTime;
      });
    }, 1000); // Update every second for smoother countdown
    
    // Set the initial check time
    lastCheckRef.current = Date.now();
  };

  // Check eligibility on component mount and when wallet changes
  useEffect(() => {
    if (!publicKey) {
      setIsFaucetAvailable(false);
      return;
    }

    const checkEligibility = async () => {
      try {
        setIsCheckingEligibility(true);
        // Check eligibility using our async function
        const eligible = await isEligibleForAirdrop(publicKey.toString());
        
        if (!eligible) {
          const remainingTime = await getTimeUntilNextAirdrop(publicKey.toString());
          // Start a timer with the remaining time
          startOrUpdateTimer(remainingTime);
        } else {
          setIsTimerActive(false);
          setTimeRemaining(0);
          setIsFaucetAvailable(true);
        }
      } catch (error) {
        console.error("Error checking eligibility:", error);
        setIsFaucetAvailable(true); // Default to available if check fails
      } finally {
        setIsCheckingEligibility(false);
      }
    };

    checkEligibility();
    
    // Clean up timer on unmount or when wallet changes
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [publicKey, connection]);

  // Handle faucet button click
  const handleFaucetClick = async () => {
    if (!publicKey || isLoading || !isFaucetAvailable) return;
    
    try {
      setIsLoading(true);
      setMessage(null);
      
      const result = await requestDevnetSol(connection, publicKey.toString());
      
      setMessage("Success! 2 SOL sent to your wallet.");
      // Start a 24-hour timer
      startOrUpdateTimer(24 * 60 * 60 * 1000);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage(null);
      }, 5000);
      
    } catch (error: any) {
      console.error("Airdrop error:", error);
      setMessage(error.message);
      
      // If this was a cooldown error, activate the timer
      if (error.message && (
        error.message.includes("airdrop limit") || 
        error.message.includes("already received") ||
        error.message.includes("Airdrop not available") ||
        error.message.includes("rate limit") ||
        error.message.includes("429") ||
        error.message.includes("try again")
      )) {
        // Record the failed attempt explicitly if not already done by the API
        if (publicKey) {
          try {
            await recordFailedAttempt(publicKey.toString());
          } catch (e) {
            console.error("Error recording failed attempt:", e);
          }
        }
        
        // Try to extract time from error message if available
        let timeToWait = 24 * 60 * 60 * 1000; // Default 24 hours
        
        if (error.message.includes("Try again in")) {
          const timePattern = /Try again in (\d+)h (\d+)m/;
          const match = error.message.match(timePattern);
          if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            timeToWait = (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
          }
        }
        
        startOrUpdateTimer(timeToWait);
      }
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setMessage(null);
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Show remaining time or button based on eligibility
  const buttonContent = () => {
    if (isCheckingEligibility) {
      return "Checking...";
    }
    if (isTimerActive && timeRemaining > 0) {
      // Format the time in a more precise way for display
      const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
      
      if (hours > 0) {
        return `Next in ${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `Next in ${minutes}m ${seconds}s`;
      } else {
        return `Next in ${seconds}s`;
      }
    }
    return (
      <>
        <img
          src="/Icons/faucet.png"
          alt="Faucet"
          className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-1.5"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}
        />
        <span style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>Faucet</span>
      </>
    );
  };

  return (
    <div>
      <button
        onClick={handleFaucetClick}
        onMouseEnter={() => setIsFaucetAnimated(true)}
        onMouseLeave={() => setIsFaucetAnimated(false)}
        disabled={!publicKey || isLoading || !isFaucetAvailable || isCheckingEligibility}
        className={`px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 overflow-visible transition-all duration-300 ease-out relative flex items-center justify-center text-xs sm:text-sm md:text-base ${
          (!publicKey || isLoading || !isFaucetAvailable || isCheckingEligibility)
            ? 'opacity-50 cursor-not-allowed'
            : ''
        }`}
        style={{
          background: (!publicKey || isLoading || !isFaucetAvailable || isCheckingEligibility)
              ? 'linear-gradient(135deg, rgba(107, 33, 168, 0.4) 25%, transparent 25%, transparent 50%, rgba(107, 33, 168, 0.4) 50%, rgba(107, 33, 168, 0.4) 75%, transparent 75%, transparent)'
              : isFaucetAnimated
                  ? 'linear-gradient(135deg, #7C3AED 25%, transparent 25%, transparent 50%, #7C3AED 50%, #7C3AED 75%, transparent 75%, transparent)'
                  : 'linear-gradient(135deg, #4C1D95 25%, transparent 25%, transparent 50%, #4C1D95 50%, #4C1D95 75%, transparent 75%, transparent)',
          backgroundSize: (!publicKey || isLoading || !isFaucetAvailable || isCheckingEligibility)
              ? '10px 10px'
              : isFaucetAnimated
                  ? '14px 14px'
                  : '12px 12px',
          border: '2px solid #7C3AED',
          borderRadius: '8px',
          color: 'white',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
          transition: 'background 0.3s ease-out, background-size 0.3s ease-out'
        }}
      >
        {isLoading ? 'Processing...' : buttonContent()}
      </button>
      
      {message && (
        <div className={`absolute mt-2 p-2 rounded-md text-sm ${
          message.includes('Success') ? 'bg-green-900/80 text-green-200' : 'bg-red-900/80 text-red-200'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
}; 